/**
 * Slash-command parser + autocomplete suggestions.
 *
 * Free text → captured as a Note (no command).
 * Leading `/` → matched against a small command table; the matched
 * command becomes the action and the remainder of the line becomes the
 * argument.
 *
 *   /ask deadline for Acme   → { kind: "ask", arg: "deadline for Acme" }
 *   /today                   → { kind: "today" }
 *   /week                    → { kind: "week" }
 *   /switch Quantara         → { kind: "switch", arg: "Quantara" }
 *   /new project             → { kind: "new_project" }
 *   /fo                      → { suggestions: [/today, …] } (autocomplete)
 *
 * The Composer calls `parseSlash(text)` on submit; if the result is a
 * command it dispatches the matching backend call, otherwise it falls
 * through to "send as note".
 */

export type SlashCommand =
  | { kind: "ask"; arg: string }
  | { kind: "today" }
  | { kind: "week" }
  | { kind: "switch"; arg: string }
  | { kind: "new_project" };

interface CommandDef {
  /** What the user types after `/`. Lowercase. */
  name: string;
  /** Short help shown in the autocomplete popover. */
  hint: string;
  /** True if the command expects an argument (rest of the line). */
  takesArg: boolean;
}

/**
 * Ordered for stable autocomplete display. Keep names short and
 * memorable — these are command-bar commands, not CLI flags.
 */
export const COMMANDS: ReadonlyArray<CommandDef> = [
  { name: "ask", hint: "Search memory — /ask <question>", takesArg: true },
  { name: "today", hint: "Today's digest", takesArg: false },
  { name: "week", hint: "Weekly digest", takesArg: false },
  { name: "switch", hint: "Switch active project — /switch <name>", takesArg: true },
  { name: "new", hint: "Create a new project — /new project", takesArg: true },
];

export type ParseResult =
  | { type: "text"; text: string }
  | { type: "command"; command: SlashCommand }
  | { type: "unknown_command"; name: string }
  | { type: "incomplete"; suggestions: ReadonlyArray<CommandDef> };

export function parseSlash(input: string): ParseResult {
  const trimmed = input.trim();
  if (!trimmed.startsWith("/")) {
    return { type: "text", text: trimmed };
  }
  // Strip the leading slash, split into command + remainder on the first space.
  const body = trimmed.slice(1);
  const firstSpace = body.indexOf(" ");
  const name = (firstSpace === -1 ? body : body.slice(0, firstSpace)).toLowerCase();
  const rest = firstSpace === -1 ? "" : body.slice(firstSpace + 1).trim();

  // Empty `/` → show all commands.
  if (!name) {
    return { type: "incomplete", suggestions: COMMANDS };
  }

  switch (name) {
    case "ask":
      // Without an argument the parse is still "incomplete" — surface
      // the hint so the user knows what's expected. Submitting an empty
      // /ask is a no-op (Composer catches it).
      if (!rest) return { type: "incomplete", suggestions: [COMMANDS[0]] };
      return { type: "command", command: { kind: "ask", arg: rest } };
    case "today":
      return { type: "command", command: { kind: "today" } };
    case "week":
      return { type: "command", command: { kind: "week" } };
    case "switch":
      if (!rest) return { type: "incomplete", suggestions: [COMMANDS[3]] };
      return { type: "command", command: { kind: "switch", arg: rest } };
    case "new":
      // We only support `/new project` for now; other "new X" land as
      // unknown so the user gets a hint.
      if (rest.toLowerCase() === "project") {
        return { type: "command", command: { kind: "new_project" } };
      }
      return { type: "incomplete", suggestions: [COMMANDS[4]] };
    default: {
      // Partial match → autocomplete the rest of the name.
      const matches = COMMANDS.filter((c) => c.name.startsWith(name));
      if (matches.length > 0) {
        return { type: "incomplete", suggestions: matches };
      }
      return { type: "unknown_command", name };
    }
  }
}
